import type { CharacterStay } from "@/data/rail-branches";
import {
  portraitUrl,
  type MarkerAppearance,
} from "../MarkerPortrait/portrait";
import { stayAt } from "../../rail/RailPose/pose";
import { railCurveMs } from "../../rail/RailLanes/lanes";
import { staysOf } from "../../rail/StayAt/stay";

/** Playhead time for one look to cover the previous one. */
export const PORTRAIT_MORPH_MS = 6_400;

export interface PortraitMorph {
  fromUrl: string | undefined;
  toUrl: string | undefined;
  /** 0 keeps the previous look. 1 is the new look. */
  reveal: number;
}

export function portraitMorphAt(
  stays: readonly CharacterStay[],
  characterId: string,
  fallback: string | undefined,
  appearances: readonly MarkerAppearance[],
  playheadMs: number,
): PortraitMorph {
  const stay = stayAt(stays, characterId, playheadMs);
  const current = portraitUrl(fallback, appearances, stay?.appearanceId);
  if (stay === undefined) {
    // Riding a split yarn to or from a scene, they already look as they do there.
    const near = nearbyStay(stays, characterId, playheadMs);
    return {
      fromUrl: portraitUrl(fallback, appearances, near?.appearanceId),
      toUrl: undefined,
      reveal: 1,
    };
  }
  const previous = stays.find(
    (item) =>
      item.characterId === characterId &&
      item.endMs === stay.startMs &&
      item.startMs < stay.startMs,
  );
  if (previous === undefined || previous.appearanceId === stay.appearanceId) {
    return { fromUrl: current, toUrl: undefined, reveal: 1 };
  }
  const reveal = smooth((playheadMs - stay.startMs) / PORTRAIT_MORPH_MS);
  if (reveal >= 1) {
    return { fromUrl: current, toUrl: undefined, reveal: 1 };
  }
  return {
    fromUrl: portraitUrl(fallback, appearances, previous.appearanceId),
    toUrl: current,
    reveal,
  };
}

/** The stay someone is riding toward, or just rode back from, within one curve's length. */
function nearbyStay(
  stays: readonly CharacterStay[],
  characterId: string,
  playheadMs: number,
): CharacterStay | undefined {
  const reach = railCurveMs();
  let best: CharacterStay | undefined;
  let bestGap = Infinity;
  for (const item of staysOf(stays, characterId)) {
    const gap = item.startMs > playheadMs ? item.startMs - playheadMs : playheadMs - item.endMs;
    if (gap >= 0 && gap <= reach && gap < bestGap) {
      best = item;
      bestGap = gap;
    }
  }
  return best;
}

/** Solid disc when nothing covers it. A portrait hides the character color. */
export function markerDiscColor(fill: string, morph: PortraitMorph): string {
  const covered = morph.fromUrl !== undefined || morph.toUrl !== undefined;
  return covered ? "transparent" : fill;
}

export function paintPortraitFaces(
  disc: HTMLElement,
  morph: PortraitMorph,
  fill: string,
): void {
  const from = disc.querySelector<HTMLElement>('[data-face="from"]');
  const to = disc.querySelector<HTMLElement>('[data-face="to"]');
  paintFace(from, morph.fromUrl, 1);
  const revealing = morph.toUrl !== undefined && morph.reveal < 1;
  paintFace(to, morph.toUrl, revealing ? morph.reveal : 0);
  disc.style.backgroundColor = markerDiscColor(fill, morph);
  const swell = revealing ? Math.sin(morph.reveal * Math.PI) * 0.08 : 0;
  disc.style.transform = swell === 0 ? "" : `scale(${(1 + swell).toFixed(4)})`;
}

/** Lets go of a hidden marker's pictures, or a phone keeps a long book's portraits decoded. */
export function releasePortraitFaces(marker: HTMLElement): void {
  for (const face of marker.querySelectorAll<HTMLElement>("[data-face]")) {
    if (face.dataset.image !== "" && face.dataset.image !== undefined) {
      face.dataset.image = "";
      face.style.backgroundImage = "";
    }
  }
}

function paintFace(
  face: HTMLElement | null,
  url: string | undefined,
  opacity: number,
): void {
  if (face === null) {
    return;
  }
  const shown = String(opacity);
  if (face.style.opacity !== shown) {
    face.style.opacity = shown;
  }
  // Every frame, for everyone. Rewriting an image that has not changed adds up on a phone.
  const image = url ?? "";
  if (face.dataset.image !== image) {
    face.dataset.image = image;
    face.style.backgroundImage = url === undefined ? "" : `url("${url}")`;
  }
}

function smooth(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
}
